import { Button, IconButton, Spinner } from '@chakra-ui/react'
import { LuDownload } from 'react-icons/lu'
import { Tooltip } from './tooltip'
import { useVideoDownload } from '../../hooks/useVideoDownload'
import { useTranslation } from '../../i18n'

/**
 * Save a video to disk.
 *
 * Pass `source` (`{ shareId }` or `{ kind, id }`) whenever the document has a
 * ready Cloudflare copy — the file then comes from Cloudflare as an MP4
 * attachment, which is both the correct format and the only path not subject
 * to the storage bucket's per-origin CORS policy. `url` is the Firebase
 * Storage fallback for videos that were never copied.
 */
export function DownloadButton({
    url,
    source,
    fileName,
    label,
    iconOnly = false,
    tooltip,
    onClick,
    ...rest
}) {
    const { t } = useTranslation()
    const { status, busy, start: handleClick } = useVideoDownload({ url, source, fileName, onDone: onClick })
    const buttonLabel = label ?? t('common.download')

    // Preparing can take a while on a long reel, so say so rather than leaving
    // a bare spinner.
    const activeTooltip =
        status === 'preparing' ? t('common.preparingDownload') : tooltip

    const button = iconOnly ? (
        <IconButton
            aria-label={status === 'preparing' ? t('common.preparingDownload') : buttonLabel}
            onClick={handleClick}
            disabled={busy}
            {...rest}
        >
            {busy ? <Spinner size="sm" /> : <LuDownload />}
        </IconButton>
    ) : (
        <Button
            variant="outline"
            onClick={handleClick}
            disabled={busy}
            {...rest}
        >
            {busy ? <Spinner size="sm" /> : <LuDownload />}
            {status === 'preparing' ? t('common.preparingDownload') : buttonLabel}
        </Button>
    )

    if (activeTooltip) {
        return <Tooltip content={activeTooltip} openDelay={300} closeDelay={100}>{button}</Tooltip>
    }

    return button
}
